import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { textFieldStyles } from "../styles/textFieldStyles";
import {
  Snackbar,
  Alert,
  Button,
  IconButton,
  Modal,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CancelIcon from "@mui/icons-material/Cancel";
import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { createTimesheet } from "../redux/actions/timesheetActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import axios from "../utils/axiosConfig";
import CancelButton from "./CancelButton";
import SubmitButton from "./SubmitButton";
import { listTimesheets } from "../redux/actions/timesheetActions";
import { smallTitle, typoBase } from "../styles/typography";
import { setNotifications } from "../redux/actions/notificationActions";

import { getFile } from "../redux/actions/fileActions";

import { getTimesheetsByCaseId } from "../redux/actions/timesheetActions";
import { useTranslation } from 'react-i18next';

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const StartTimerButton = () => {
  const { t } = useTranslation(['common', 'buttons', 'pages', 'forms', 'messages']);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  const [intervalId, setIntervalId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { timesheets } = useSelector((state) => state.timesheetList);
  const { file } = useSelector((state) => state.fileDetails);
  const [id, setId] = useState(location.state?.caseId);

  const files = useSelector((state) => state.fileList.files || []);
  const relationships = useSelector(
    (state) => state.relationshipList.relationships || []
  );
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [ownerHourlyRate, setOwnerHourlyRate] = useState(
    user ? user.hourlyRate : 100
  ); // Default to 100 if no owner is selected
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  const [formData, setFormData] = useState({
    owner: "",
    type: t('forms:types.working_time'),
    description: "",
    date: getCurrentDate(),
    case: location.state?.caseId || "",
    billable: true,
    rate: 0, // Changed from ownerHourlyRate to 0
  });

  const [billingItems, setBillingItems] = useState([
    {
      type: t('forms:types.working_time'), // Default value
      description: "", // Default value
      rate: ownerHourlyRate, // Default value
      quantity: "0:00", // Default value
      billable: true, // Default value
    },
  ]);

  useEffect(() => {
    if (user) {
      setFormData({ ...formData, owner: user.relationship });
      setOwnerHourlyRate(user.hourlyRate);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchFiles());
    dispatch(fetchRelationships());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleOwnerChange = async (event, newValue) => {
    const ownerId = newValue?._id || "";
    setFormData({ ...formData, owner: ownerId });

    if (ownerId) {
      try {
        const response = await axios.get(`/api/relationships/${ownerId}`);
        const owner = response.data;
        if (owner.contractType === "Individual") {
          setOwnerHourlyRate(owner.hourlyRate); // Set the owner's hourly rate
          setFormData({ ...formData, rate: owner.hourlyRate });
        }
      } catch (error) {
        console.error("Error fetching owner details:", error);
      }
    }
  };

  const handleCaseChange = (event, newValue) => {
    const fileId = newValue?._id || "";
    const selectedFile = files.find(file => file._id === fileId);
    
    // Get rate from file configuration
    const fileRate = selectedFile?.rateConfig?.type === 'hourly' 
      ? selectedFile.rateConfig.hourlyRate 
      : 0;
  
    setFormData({
      ...formData,
      case: fileId,
      rate: fileRate // Set rate from file configuration
    });
  };

  function parseDurationToHours(timeString) {
    const [hh, mm, ss] = timeString.split(":").map(Number);
    return hh + mm / 60 + ss / 3600;
  }

  const { loading, success, error } = useSelector(
    (state) => state.timesheetCreate
  );

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const runAsync = async () => {
      if (success && timerModalOpen) {
        setNotification({
          open: true,
          message: t('messages:info.timesheets_created_successfully'),
          severity: "success",
        });
        dispatch({ type: "TIMESHEET_CREATE_RESET" }); // Reset the success state
        dispatch(fetchFiles());
        dispatch(getFile(id));

        dispatch(getTimesheetsByCaseId(id));
        // Then compare sums with file's alarm settings

        setTimeout(() => {}, 2000);
        // console.log("timesheets", timesheets);
        const sumHours = timesheets?.reduce((acc, ts) => {
          return acc + parseDurationToHours(ts.totalDuration);
        }, 0);
        const sumFees = timesheets?.reduce(
          (acc, ts) => acc + ts.totalAmount,
          0
        );
        console.log("sumHours", sumHours);
        const hourLimit = files.find(
          (file) => file._id === formData.case
        )?.signalingHours;
        const feeLimit = files.find(
          (file) => file._id === formData.case
        )?.limitOnFees;
        const percent =
          files.find((file) => file._id === formData.case)?.warningThreshold /
          100;
        console.log("hh", hourLimit);
        let showNotification = false;
        console.log(
          files.find((file) => file._id === formData.case)?.notificationShown
        );
        if (
          !files.find((file) => file._id === formData.case)?.notificationShown
        ) {
          if (
            sumHours >= hourLimit * (0.01 * percent) ||
            sumFees >= feeLimit * (0.01 * percent)
          ) {
            showNotification = true;
          }
        }
        console.log("showNotification", showNotification);
        if (showNotification) {
          // Trigger UI notification
          // console.log("Warning: You have exceeded the configured limits.");
          // Mark notification as shown in the DB
          await axios.put(`/api/files/${id}`, { notificationShown: true });
          await axios.post("/api/notifications", {
            user: user?.relationship,
            type: "File",
            subject: t('messages:info.work_limit_exceeded_subject'),
            file: id,
          });
          dispatch(getFile(id)); // refresh store
          const res = await axios.get(`/api/notifications/${user._id}`);
          setTimeout(() => {}, 2000);
          dispatch(setNotifications(res.data));
        }
        // setTimeout(() => {
        //   navigate(-1);
        // }, 1000);
        // window.location.reload(true);
        // navigate('/documents');
      } else if (error) {
        setNotification({
          open: true,
          message: error,
          severity: "error",
        });
        dispatch({ type: "TIMESHEET_CREATE_RESET" }); // Reset the error state
      }

      setTimerModalOpen(false);
    };
    runAsync();
  }, [success, error, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setId(formData.case);
    // Update billingItems with the new values
    const updatedBillingItems = [
      {
        ...billingItems[0],
        rate: formData.rate,
        quantity: formatTime(timer),
        billable: formData.billable,
      },
    ];
    try {
      // Dispatch createTimesheet with the formData and updatedBillingItems
      await dispatch(
        createTimesheet({
          formData,
          billingItems: updatedBillingItems,
          totalDuration: formatTime(timer),
          totalAmount: ((timer / 3600) * formData.rate).toFixed(2),
        })
      );
      setSnackbarMessage(t('messages:success.timesheet_created'));
      setSnackbarSeverity("success");
    } catch (error) {
      setTimerModalOpen(false);
      setSnackbarMessage(t('messages:info.failed_to_create_timesheet'));
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      // Fetch the latest timesheets
      dispatch(listTimesheets());
      // navigate("/timesheets");
      setTimer(0);
    }
  };

  const handleCancel = () => {
    setTimerModalOpen(false);
    setTimer(0);
    navigate("/timesheets");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const startTimer = () => {
    setIsRunning(true);
    const id = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    setIntervalId(id);
  };

  const stopTimer = () => {
    setIsRunning(false);
    clearInterval(intervalId);
  };

  const resumeTimer = () => {
    setIsRunning(true);
    const id = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    setIntervalId(id);
  };

  const cancelTimer = () => {
    setIsRunning(false);
    clearInterval(intervalId);
    setTimer(0);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOpenTimerModal = () => {
    setTimerModalOpen(true);
  };

  const handleCloseTimerModal = () => {
    setTimerModalOpen(false);
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (userRole === "Manager") {
      return true; // Managers can see all files
    } else if (userRole === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        return (
          member?.member?._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (userRole === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const inputStyles = { width: "100%", marginBottom: 2 };
  const buttonStyles = { margin: "0 8px" };

  return (
    <>
      <Button
        onClick={handleOpenTimerModal}
        startIcon={<PlayArrowIcon />}
        sx={{
          padding: "2px 20px",
          borderRadius: "0",
          textTransform: "capitalize",
          ...typoBase,
          fontSize: "10.5px",
          fontFamily:
            "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
        }}
      >
        {t('buttons:primary.timer')}
      </Button>
      <Modal open={timerModalOpen} onClose={handleCloseTimerModal}>
        <Box sx={{ ...modalStyle, width: 400 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            {t('pages:timesheets.createTimesheet')}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              {formatTime(timer)}
            </Typography>
            <Box sx={{ ml: 2 }}>
              {!isRunning ? (
                <Tooltip title={timer === 0 ? t('buttons:primary.start') : t('buttons:primary.resumeTimer')}>
                  <IconButton color="primary" onClick={startTimer}>
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('buttons:primary.pauseTimer')}>
                  <IconButton color="secondary" onClick={stopTimer}>
                    <PauseIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('buttons:primary.resetTimer')}>
                <IconButton
                  color="error"
                  onClick={cancelTimer}
                  sx={buttonStyles}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <FormControl sx={{ width: "100%" }} required>
                  <CustomAutocomplete
                    options={relationships}
                    value={
                      relationships.find((rel) => rel._id === formData.owner) ||
                      null
                    }
                    onChange={handleOwnerChange}
                    label={t('forms:labels.owner')}
                    filterType={t('forms:filterTypes.member')}
                    addNewLabel={t('forms:addLabels.addOwner')}
                    addNewPath="/relationships/create"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={t('forms:activityTypes', { returnObjects: true })}
                  getOptionLabel={(option) => option.label}
                  onChange={(event, newValue) =>
                    handleChange({
                      target: { name: "type", value: newValue?.label || "" },
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forms:labels.code')}
                      size="small"
                      sx={textFieldStyles()}
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <TextField
                  type="date"
                  label="Date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid> */}
              <Grid item xs={12}>
                <FileAutocomplete
                  options={filteredFiles}
                  value={
                    files.find((file) => file._id === formData.case) || null
                  }
                  onChange={handleCaseChange}
                  label={t('forms:labels.case')}
                  name="case"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('forms:labels.description')}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  fullWidth
                  sx={textFieldStyles()}
                  size="small"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.billable}
                      onChange={handleChange}
                      name="billable"
                    />
                  }
                  sx={textFieldStyles()}
                  label={t('forms:labels.billable')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('forms:labels.rate')}
                  name="rate"
                  value={formData.rate}
                  onChange={handleChange}
                  fullWidth
                  sx={textFieldStyles()}
                  size="small"
                  disabled={!files.find(f => f._id === formData.case)?.rateConfig?.type === 'hourly'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
              >
                <CancelButton
                  variant="outlined"
                  onClick={handleCancel}
                  size="small"
                >
                  {t('buttons:secondary.cancel')}
                </CancelButton>
                <SubmitButton onClick={handleSubmit} size="small">
                  {t('pages:timesheets.createTimesheet')}
                </SubmitButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        sx={{ zIndex: 9999, position: "fixed" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%", zIndex: 9999999 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #0067C0",
  boxShadow: 24,
  p: 4,
};



export default StartTimerButton;
